import {
  AI_SOLUTION_TYPES,
  getPilotRecommendation,
} from './aiPilotRecommendation';
import {
  computeChecklistScore,
  formatAnswerValue,
  getPillarScores,
  getReadinessFromBands,
  isScoredBlock,
  isScoredQuestion,
} from './checklistUtils';

const OPERATIONS_READINESS_SCALE_BANDS = [
  { id: 'prep', emoji: '❌', label: 'Сначала подготовка', range: '0–12', min: 0 },
  { id: 'limited', emoji: '⚠️', label: 'Пилот с ограничениями', range: '13–24', min: 13 },
  { id: 'pilot-ready', emoji: '✅', label: 'Можно планировать пилот', range: '25–36', min: 25 },
];

function buildOperationsReadinessScale(score, readinessBands) {
  const active = getReadinessFromBands(score, readinessBands);
  return OPERATIONS_READINESS_SCALE_BANDS.map((band) => ({
    ...band,
    active: active.min === band.min,
  }));
}

function buildOperationsEmailBlocks(checklistData, answers, scale, scaleMax) {
  return checklistData.blocks.map((block) => {
    const questions = block.questions.map((question) => {
      if (question.type === 'multi_choice') {
        return {
          text: question.text,
          score: 0,
          maxScore: 0,
          answerText: formatAnswerValue(answers[question.id], scale, question),
        };
      }

      const questionScore = Number(answers[question.id]) || 0;
      const answerText = formatAnswerValue(answers[question.id], scale, question);
      return {
        text: question.text,
        score: questionScore,
        maxScore: scaleMax,
        answerText: `${answerText} (${questionScore} / ${scaleMax})`,
      };
    });

    let score = 0;
    let maxScore = 0;
    if (isScoredBlock(block)) {
      block.questions.forEach((question) => {
        if (!isScoredQuestion(question, block)) return;
        score += Number(answers[question.id]) || 0;
        maxScore += scaleMax;
      });
    }

    return {
      id: block.pillar || String(block.id),
      title: block.title,
      description: block.description,
      score,
      maxScore,
      questions,
    };
  });
}

function buildOperationsPilotSection({ answers, score, pillarScores }) {
  const recommendation = getPilotRecommendation({
    goals: answers.automation_goals,
    totalScore: score,
    pillarScores,
  });

  if (!recommendation.startType) {
    return {
      title: 'С чего начать пилот',
      solutionType: '—',
      summary: recommendation.summary,
      bullets: recommendation.bullets,
      linkLabel: 'Подробнее: классификация ИИ-решений →',
      linkHref: recommendation.blogUrl,
    };
  }

  return {
    title: 'С чего начать пилот',
    solutionType: AI_SOLUTION_TYPES[recommendation.startType]?.title || recommendation.startType,
    summary: recommendation.summary,
    bullets: recommendation.bullets,
    linkLabel: 'Подробнее: классификация ИИ-решений →',
    linkHref: recommendation.blogUrl,
  };
}

/**
 * Данные для HTML-письма operations-чек-листа (/ready-for-ai).
 */
export function buildChecklistOperationsEmailData({
  pageConfig,
  answers,
  checklistData,
  lead,
  meta,
  readinessBands,
  scale,
}) {
  const score = answers._score ?? computeChecklistScore(checklistData, answers);
  const readiness = getReadinessFromBands(score, readinessBands);
  const scaleMax = meta.scaleMax ?? 2;
  const pillarScores = getPillarScores(checklistData, answers, scaleMax);
  const resultConfig = pageConfig.result;

  return {
    eyebrow: 'Чек-лист',
    title: pageConfig.reportTitle || 'Готовность компании к пилоту ИИ',
    score,
    maxScore: meta.maxScore,
    readinessLabel: readiness.label,
    readinessEmoji: readiness.emoji,
    user: {
      name: lead.name?.trim() || '—',
      company: lead.company?.trim() || '—',
      email: lead.email?.trim() || '—',
    },
    blocks: buildOperationsEmailBlocks(checklistData, answers, scale, scaleMax),
    pilot: buildOperationsPilotSection({ answers, score, pillarScores }),
    readinessScale: buildOperationsReadinessScale(score, readinessBands),
    cta: {
      title: resultConfig.ctaHeading || resultConfig.ctaTitle || 'Разобрать результат с менеджером',
      subtitle:
        resultConfig.ctaSubtext ||
        resultConfig.ctaText ||
        'Обсудим рамки пилота и расчёт внедрения AI в ваш бизнес',
      buttonLabel: 'Обсудить внедрение AI',
    },
  };
}
